import {
    BillingAccountOrderFields,
    BucketOrderFields,
    InvoiceOrderFields,
    IpAddressOrderFields,
    LoadBalancerOrderFields,
    ServicePackageOrderFields,
    VmOrderFields,
} from "@/admin/graphql.gen/graphql.ts";
import { buildTableDetailSearch } from "@/admin/itemLinks.ts";
import { link, type RouterLinkProps } from "@/components/Action.tsx";
import { ActionMenu } from "@/components/ActionMenu.tsx";
import { WModalButton, type WModalButtonProps } from "@/components/button/WToolButton.tsx";
import { getResourceIconClass, resourceTypeEnabledAtom, type ResourceType } from "@/modules/api/resourceTypes.tsx";
import { useAtomValue } from "jotai/react";
import { atom } from "jotai/vanilla";

/** Represents links to related entities from some other entity (eg. user, account) */
interface Related {
    label: string;
    type: ResourceType | undefined;
    icon?: string;

    // These functions are undefined if a link from that entity type is not applicable

    fromUser?: (id: string) => RouterLinkProps;
    fromAccount?: (id: string) => RouterLinkProps;
}

type FromType = "fromUser" | "fromAccount";

const related: Related[] = [
    {
        label: "Billing accounts",
        type: "billing_account",
        fromUser: (id) =>
            link({
                to: "/admin/accounts",
                search: buildTableDetailSearch(BillingAccountOrderFields.UserId, id),
            }),
    },
    {
        label: "Virtual machines",
        type: "virtual_machine",
        fromUser: (id) =>
            link({
                to: "/admin/virtual_machines",
                search: buildTableDetailSearch(VmOrderFields.UserId, id),
            }),
        fromAccount: (id) =>
            link({
                to: "/admin/virtual_machines",
                search: buildTableDetailSearch(VmOrderFields.BillingAccount, id),
            }),
    },
    {
        label: "Load balancers",
        type: "load_balancer",
        fromUser: (id) =>
            link({
                to: "/admin/load_balancers",
                search: buildTableDetailSearch(LoadBalancerOrderFields.UserId, id),
            }),
        fromAccount: (id) =>
            link({
                to: "/admin/load_balancers",
                search: buildTableDetailSearch(LoadBalancerOrderFields.BillingAccountId, id),
            }),
    },
    {
        label: "Object storage",
        type: "bucket",
        fromUser: (id) =>
            link({
                to: "/admin/buckets",
                search: buildTableDetailSearch(BucketOrderFields.UserId, id),
            }),
        fromAccount: (id) =>
            link({
                to: "/admin/buckets",
                search: buildTableDetailSearch(BucketOrderFields.BillingAccountId, id),
            }),
    },
    {
        label: "Floating IPs",
        type: "ip_address",
        fromUser: (id) =>
            link({
                to: "/admin/floating_ips",
                search: buildTableDetailSearch(IpAddressOrderFields.UserId, id),
            }),
        fromAccount: (id) =>
            link({
                to: "/admin/floating_ips",
                search: buildTableDetailSearch(IpAddressOrderFields.BillingAccountId, id),
            }),
    },
    {
        label: "Services",
        type: "managed_service",
        fromUser: (id) =>
            link({
                to: "/admin/service_packages",
                search: buildTableDetailSearch(ServicePackageOrderFields.UserId, id),
            }),
        fromAccount: (id) =>
            link({
                to: "/admin/service_packages",
                search: buildTableDetailSearch(ServicePackageOrderFields.BillingAccountId, id),
            }),
    },
    {
        label: "Invoices",
        // XXX: Create a resource type for invoices?
        type: undefined,
        icon: "jp-to-do-icon",
        fromUser: (id) =>
            link({
                to: "/admin/reports",
                search: buildTableDetailSearch(InvoiceOrderFields.BillingAccountUserId, id),
            }),
        fromAccount: (id) =>
            link({
                to: "/admin/reports",
                search: buildTableDetailSearch(InvoiceOrderFields.BillingAccountId, id),
            }),
    },
];

const enabledRelated = atom((get) => related.filter(({ type }) => type == null || get(resourceTypeEnabledAtom(type))));

export function RelatedItems({
    id,
    from,
    ...props
}: { id: string; from: FromType } & Omit<WModalButtonProps, "label">) {
    const related = useAtomValue(enabledRelated);

    return (
        <ActionMenu
            items={() =>
                related
                    .filter((item) => item[from])
                    .map((item) => {
                        const icon = item.icon ?? (item.type ? getResourceIconClass(item.type) : "");
                        return {
                            id: item.label,
                            title: item.label,
                            icon: `${icon} color-primary size-1rem`,
                            action: item[from]!(id),
                        };
                    })
            }
        >
            <WModalButton
                label="View related"
                icon={props.inTable ? "i-lucide:link" : "i-lucide:chevron-down"}
                {...props}
            />
        </ActionMenu>
    );
}
